import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Alert,
  Stack,
  Typography,
  InputAdornment,
} from "@mui/material";
import { MdPerson, MdPhone, MdTwoWheeler, MdBadge } from "react-icons/md";

import { T } from "theme/tokens";
import * as api from "./api";

/**
 * Edit the fields an admin is allowed to change on a delivery partner.
 *
 * These four mirror the backend's allow-list exactly — anything else is
 * rejected server-side, and offering an input for a field that silently will
 * not save is worse than not offering it. Bank details and the wallet balance
 * are deliberately absent: both have their own audited screens, and the
 * balance is money owed that only a transaction may move.
 *
 * Only changed fields are sent, so the audit log records what someone
 * actually edited rather than one row per field on the form.
 */
const FIELDS = [
  { key: "fullname", label: "Full name", icon: <MdPerson size={18} /> },
  {
    key: "phone",
    label: "Phone",
    icon: <MdPhone size={18} />,
    help: "This is the partner's login. Changing it changes how they sign in.",
  },
  { key: "vehicleNumber", label: "Vehicle number", icon: <MdTwoWheeler size={18} /> },
  { key: "drivingLicense", label: "Driving licence", icon: <MdBadge size={18} /> },
];

const blank = () => FIELDS.reduce((acc, f) => ({ ...acc, [f.key]: "" }), {});

export default function EditPartnerDialog({ open, partner, onClose, onSaved }) {
  const [values, setValues] = useState(blank);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open || !partner) return;
    setError("");
    setSaving(false);
    setValues(
      FIELDS.reduce((acc, f) => ({ ...acc, [f.key]: partner[f.key] ?? "" }), {})
    );
  }, [open, partner]);

  const changed = FIELDS.filter(
    (f) => String(values[f.key] ?? "") !== String(partner?.[f.key] ?? "")
  );
  const phoneChanged = changed.some((f) => f.key === "phone");

  const save = async () => {
    setError("");
    setSaving(true);
    try {
      const body = changed.reduce(
        (acc, f) => ({ ...acc, [f.key]: values[f.key] }),
        {}
      );
      await api.updateProfile(partner.partnerId, body);
      onSaved?.(`${changed.length} field(s) updated.`);
      onClose();
    } catch (err) {
      setError(
        err?.response?.data?.message || err?.message || "Could not save those changes."
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3 } }}
    >
      <DialogTitle sx={{ pb: 0.5 }}>
        <Typography sx={{ fontSize: "1.05rem", fontWeight: 700, color: T.ink }}>
          Edit {partner?.fullname || "delivery partner"}
        </Typography>
        <Typography sx={{ fontSize: "0.78rem", color: T.muted, fontFamily: "monospace" }}>
          {partner?.partnerId}
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ pt: 2 }}>
        <Stack spacing={2}>
          <Alert severity="info" sx={{ borderRadius: 2, fontSize: "0.82rem" }}>
            Bank details are edited in the partner console, and the wallet
            balance only moves through a payout — neither can be set here.
          </Alert>

          <Grid container spacing={2} sx={{ pt: 0.5 }}>
            {FIELDS.map((field) => (
              <Grid item xs={12} sm={6} key={field.key}>
                <TextField
                  fullWidth
                  size="small"
                  label={field.label}
                  value={values[field.key] ?? ""}
                  onChange={(e) =>
                    setValues((v) => ({ ...v, [field.key]: e.target.value }))
                  }
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">{field.icon}</InputAdornment>
                    ),
                  }}
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                />
              </Grid>
            ))}
          </Grid>

          {/* Not a validation error — a warning about a consequence that is
              not obvious from a text field labelled "Phone". */}
          {phoneChanged && (
            <Alert severity="warning" sx={{ borderRadius: 2, fontSize: "0.82rem" }}>
              The phone number is this partner's login. After saving, they can
              only sign in with the new number.
            </Alert>
          )}

          {error && <Alert severity="error">{error}</Alert>}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Typography sx={{ fontSize: "0.78rem", color: T.subtle, mr: "auto" }}>
          {changed.length ? `${changed.length} change(s) pending` : "Nothing changed yet"}
        </Typography>
        <Button onClick={onClose} disabled={saving} sx={{ textTransform: "none", color: T.muted }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={save}
          disabled={saving || !changed.length}
          sx={{
            textTransform: "none",
            borderRadius: 2,
            bgcolor: T.primary,
            "&:hover": { bgcolor: "#013838" },
          }}
        >
          {saving ? "Saving…" : "Save changes"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
